import { getExpressInterestButtonText } from "repoV2/features/ExpressInterest/utils/getExpressInterestButtonText";
import { LISTING_TYPES } from "repoV2/features/Listing/Listing.constants";
import { checkout_form_types } from "../constants/CheckoutPages.constants";
import { useCheckoutBasicDetailsContext } from "../modules/CheckoutBasicDetailsContextProvider";
import {
    useHasSessionDetailsFetched,
    useHideSessionDetailsFormGroup,
} from "../modules/PaymentDetailsSection/modules/FormGroups/modules/SessionDetailsFormGroup/SessionDetailsFormGroup.utils";

/**
 * This custom hook is used to check which form to show to the user, and when to show it based upon 
 * the required api's loading, and the form title
 * @returns an object containing the following
 - areFormGroupsLoading { boolean } - incase we have to show shimmer to user, as we are yet to determince which form will be shown,
 * usually incase of listing which call session api to get session/ classes
 - formToRender { string } - which form to show to user - payment | express interest | renew subscription
 - formTitle { string } - title of the form which is based upon the form we show to user
 */
const useCheckoutFormRenderState = () => {
    const {
        listingUuid,
        listingType,
        listingStatus,
        isListingExpressInterest,
        isRenewSubscription,
        hideListingPrice,
        isShiprocketEnabled,
        purchasedMerchandiseInventory,
        totalMerchandiseInventory,
    } = useCheckoutBasicDetailsContext();

    const { hideSessionDetailsFormGroup } = useHideSessionDetailsFormGroup({
        listingType,
        listingStatus,
    });

    const hasSessionDetailsFetched = useHasSessionDetailsFetched({
        listingUuid,
    });

    const areFormGroupsLoading =
        !hideSessionDetailsFormGroup && !hasSessionDetailsFetched;

    // If it's not a Shiprocket scenario and there is no inventory available, the merchandise is considered sold out.
    // If variants exist, the stock information is only shown after the user selects a variant, so no need of a check here
    // in that case
    const isMerchandiseSoldout =
        listingType === LISTING_TYPES.MERCHANDISE &&
        !isShiprocketEnabled &&
        !(totalMerchandiseInventory - purchasedMerchandiseInventory);

    if (isListingExpressInterest) {
        return {
            areFormGroupsLoading,
            formToRender: checkout_form_types.express_interest_form,
            formTitle: getExpressInterestButtonText({
                listingStatus,
                hideListingPrice,
                isMerchandiseSoldout,
            }),
        };
    }

    if (isRenewSubscription) {
        return {
            areFormGroupsLoading,
            formToRender: checkout_form_types.renew_subscription_form,
            formTitle: "Payment Details",
        };
    }
    return {
        areFormGroupsLoading,
        formToRender: checkout_form_types.payment_form,
        formTitle: "Payment Details",
    };
};

export default useCheckoutFormRenderState;
